/**
 * Links each .pdp-bundle-collapsible trigger to the following MBC app block.
 * MBC must stay a sibling (not nested) so mbcBundleItemsWidget can mount.
 *
 * Horizon nests theme blocks directly in `.group-block-content` (no per-block
 * shopify-block wrapper), while app blocks do get `.shopify-block`. Walk next
 * siblings from the shell itself first.
 */

const PANEL_KEY = '_pdpBundlePanel';

/**
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function looksLikeMbc(el) {
  if (el.id === 'mbcBundleItemsWidget') return true;
  if (/mbc/i.test(el.id) && (el.classList.contains('shopify-block') || el.classList.contains('shopify-app-block'))) {
    return true;
  }
  return Boolean(el.querySelector('#mbcBundleItemsWidget, [id*="mbcBundle" i], [id*="mbc-bundle" i], [id*="mbc_bundle" i]'));
}

/**
 * @param {HTMLElement} start
 * @returns {HTMLElement | null}
 */
function findBundlePanelFrom(start) {
  let sibling = start.nextElementSibling;
  while (sibling) {
    if (sibling instanceof HTMLElement && looksLikeMbc(sibling)) {
      return sibling;
    }
    sibling = sibling.nextElementSibling;
  }
  return null;
}

/**
 * @param {HTMLElement} shell
 * @returns {HTMLElement | null}
 */
function findBundlePanel(shell) {
  // Theme blocks sit bare in group-block-content; MBC is the next shopify-app-block.
  const direct = findBundlePanelFrom(shell);
  if (direct) return direct;

  // Fallback if the trigger is wrapped (e.g. shopify-block in other contexts).
  let node = shell.parentElement;
  while (node && node !== document.body) {
    const panel = findBundlePanelFrom(node);
    if (panel) return panel;
    if (node.classList.contains('product-details') || node.classList.contains('shopify-section')) break;
    node = node.parentElement;
  }

  return null;
}

/**
 * @param {HTMLElement} shell
 * @returns {HTMLElement | null}
 */
function resolvePanel(shell) {
  const panel = findBundlePanel(shell);
  shell[PANEL_KEY] = panel;
  if (panel) bindCarouselScrollLock(panel);
  return panel;
}

/**
 * Keep page scroll locked while MBC keen-slider changes slides.
 * Otherwise focus/layout shifts can jump the panel out of view.
 * @param {HTMLElement} panel
 */
function bindCarouselScrollLock(panel) {
  if (panel.dataset.carouselScrollBound === 'true') return;
  panel.dataset.carouselScrollBound = 'true';

  panel.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('.mbc-bundle-arrow')) return;

      const y = window.scrollY;
      const restore = () => window.scrollTo(0, y);
      requestAnimationFrame(() => {
        restore();
        requestAnimationFrame(restore);
        window.setTimeout(restore, 50);
        window.setTimeout(restore, 150);
        window.setTimeout(restore, 320);
      });
    },
    true
  );
}

/**
 * @param {HTMLElement} shell
 * @param {HTMLElement | null} panel
 * @param {boolean} open
 */
function sync(shell, panel, open) {
  shell.dataset.open = open ? 'true' : 'false';
  const button = shell.querySelector('[data-pdp-bundle-toggle]');
  button?.setAttribute('aria-expanded', open ? 'true' : 'false');

  if (!panel) return;

  panel.classList.add('pdp-bundle-panel');
  panel.classList.toggle('pdp-bundle-panel--themed', shell.dataset.themeStyleMbc !== 'false');
  panel.dataset.collapsed = open ? 'false' : 'true';

  if (button && panel.id) {
    button.setAttribute('aria-controls', panel.id);
  }

  // Inherit appearance tokens from the trigger (panel is a sibling, not a child).
  const styles = getComputedStyle(shell);
  ['--pdp-accent', '--pdp-border', '--pdp-radius', '--pdp-surface', '--pdp-text'].forEach((prop) => {
    const value = styles.getPropertyValue(prop).trim();
    if (value) panel.style.setProperty(prop, value);
  });

  if (open) {
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }
}

/**
 * @param {HTMLElement} shell
 */
function bindShell(shell) {
  if (shell.dataset.bundleBound === 'true') {
    // Refresh panel reference if MBC mounted later.
    if (!shell[PANEL_KEY] || !document.contains(shell[PANEL_KEY])) {
      resolvePanel(shell);
    }
    return;
  }

  shell.dataset.bundleBound = 'true';

  // Let MBC mount while still in the layout, then collapse.
  requestAnimationFrame(() => {
    window.setTimeout(() => {
      const panel = resolvePanel(shell);
      if (shell.dataset.open !== 'true') {
        sync(shell, panel, false);
      }
    }, 120);
  });
}

function initAll() {
  document.querySelectorAll('[data-pdp-bundle-shell]').forEach((shell) => {
    if (shell instanceof HTMLElement) bindShell(shell);
  });
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest('[data-pdp-bundle-toggle]');
  if (!button) return;

  const shell = button.closest('[data-pdp-bundle-shell]');
  if (!(shell instanceof HTMLElement)) return;

  event.preventDefault();
  event.stopPropagation();

  const panel = resolvePanel(shell);
  const next = shell.dataset.open !== 'true';
  sync(shell, panel, next);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

document.addEventListener('shopify:section:load', initAll);
document.addEventListener('shopify:block:select', initAll);

const observer = new MutationObserver(() => {
  document.querySelectorAll('[data-pdp-bundle-shell]').forEach((shell) => {
    if (!(shell instanceof HTMLElement)) return;
    if (!shell[PANEL_KEY] || !document.contains(shell[PANEL_KEY])) {
      resolvePanel(shell);
    }
  });
});

if (document.body) {
  observer.observe(document.body, { childList: true, subtree: true });
} else {
  document.addEventListener('DOMContentLoaded', () => {
    observer.observe(document.body, { childList: true, subtree: true });
  });
}
